import { Link, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  Sparkles, 
  // Heart, 
  User, 
  Home,
  Menu,
  X,
  Crown,
  BarChart3,
  FileText,
  Users,
  Users2,
  LogOut,
  Wifi,
  WifiOff,
  // Star,
  MessageCircle,
  Coins
} from 'lucide-react'
import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import SubscriptionStatus from './SubscriptionStatus'
import UserStatusModal from './UserStatusModal'
import RraasiCoinDisplay from './RraasiCoinDisplay'
import { authService } from '../services/authService'

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false)
  const [isImporting, setIsImporting] = useState(false)
  const location = useLocation()
  const { user, signOut, userStatus, checkUserStatus } = useAuth()

  // Check user status when component mounts or user changes
  useEffect(() => {
    if (user?.uid) {
      // Only check status if we don't have it yet or if it's been a while
      if (!userStatus || Date.now() - (userStatus as any).lastCheck > 30000) {
        checkUserStatus(user.uid)
      }
    }
  }, [user?.uid, checkUserStatus, userStatus])

  // Handle auto-import of missing charts
  const handleAutoImport = async () => {
    if (!user?.uid) return
    
    setIsImporting(true)
    try {
      // Call the activate astro agents API
      const response = await authService.activateAstroAgents(user.uid)
      
      if (response.success) {
        // Force a status check immediately after activation
        await checkUserStatus(user.uid, false, true) // Force refresh
      } else {
        console.error('Failed to activate astro agents:', response.error)
      }
    } catch (error) {
      console.error('Auto-import failed:', error)
    } finally {
      setIsImporting(false)
    }
  }

  const navItems = [
    { path: '/', label: 'Home', icon: Home },
    { path: '/astrology', label: 'Astrology', icon: Sparkles },
    { path: '/group-compatibility', label: 'Relationships', icon: Users2 },
    // { path: '/wellness', label: 'Wellness', icon: Heart },
    { path: '/matchmaking', label: 'Matchmaking', icon: Users },
    // { path: '/voice-assistant', label: 'Voice', icon: Mic },
    { path: '/user-journey', label: 'Analytics', icon: BarChart3 },
    { path: '/journey-report', label: 'Report', icon: FileText },
    { path: '/subscription', label: 'Subscription', icon: Crown },
    { path: '/rraasi-coins', label: 'RRAASI Coins', icon: Coins },
    { path: '/profile', label: 'Profile', icon: User },
    { path: '/reports', label: 'Reports', icon: FileText },
    { path: '/chat', label: 'Chat', icon: MessageCircle }
  ]

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className="glass-effect-strong sticky top-0 z-50 backdrop-blur-lg border-b border-white/20 hidden lg:block"
    >
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-12">
          {/* Logo/Title */}
          <Link to="/" className="flex items-center mobile-focus">
            <span className="text-2xl font-black bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent tracking-tight">
              RRAASI
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            {navItems.map((item) => {
              const Icon = item.icon
              const isActive = location.pathname === item.path
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center space-x-1 px-2.5 py-1.5 rounded-lg transition-all duration-200 mobile-focus text-sm ${
                    isActive
                      ? 'bg-white/20 text-white mobile-shadow'
                      : 'text-gray-300 hover:text-white hover:bg-white/10'
                  }`}
                >
                  <Icon className="w-3 h-3" />
                  <span className="font-medium">{item.label}</span>
                </Link>
              )
            })}
          </div>

          {/* User Status & Mobile Menu Button */}
          <div className="flex items-center space-x-2">
            {/* User Status - Desktop */}
            <div className="hidden md:flex items-center space-x-2">
              {user ? (
                <div className="flex items-center space-x-2">
                  <SubscriptionStatus />
                  
                  {/* RRAASI Coin Display */}
                  <RraasiCoinDisplay compact={true} />
                  
                  {/* User Status Indicator */}
                  <button
                    onClick={() => setIsStatusModalOpen(true)}
                    className="flex items-center space-x-1 px-2 py-1 rounded-lg transition-all duration-200 hover:scale-105"
                    title={userStatus?.astroAgentsLive ? `${userStatus.astroAgentsLive} Astro Agents live` : 'No Astro Agents available'}
                  >
                    {userStatus?.isOnline ? (
                      <div className="flex items-center space-x-1 px-2 py-1 rounded-lg bg-green-500/20 border border-green-500/30">
                        <Wifi className="w-3 h-3 text-green-400" />
                        <span className="text-xs text-green-400 font-medium">
                          {userStatus?.astroAgentsLive > 0 ? `${userStatus.astroAgentsLive} Astro Agents` : 'Online'}
                        </span>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-1 px-2 py-1 rounded-lg bg-red-500/20 border border-red-500/30">
                        <WifiOff className="w-3 h-3 text-red-400" />
                        <span className="text-xs text-red-400 font-medium">
                          {userStatus?.astroAgentsLive > 0 ? `${userStatus.astroAgentsLive} Astro Agents` : 'Offline'}
                        </span>
                      </div>
                    )}
                  </button>
                  
                  <div className="w-6 h-6 bg-gradient-to-r from-green-500 to-blue-500 rounded-full flex items-center justify-center mobile-shadow">
                    <span className="text-white text-xs font-medium">
                      {user.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <span className="text-gray-300 text-sm font-medium">{user.name}</span>
                  <button
                    onClick={signOut}
                    className="p-1.5 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-all duration-200"
                    title="Sign Out"
                  >
                    <LogOut className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <Link
                    to="/login"
                    className="px-3 py-1.5 text-gray-300 hover:text-white text-sm font-medium transition-all duration-200"
                  >
                    Sign In
                  </Link>
                  <Link
                    to="/signup"
                    className="px-3 py-1.5 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-sm font-medium rounded-lg hover:from-purple-600 hover:to-pink-600 transition-all duration-200"
                  >
                    Get Started
                  </Link>
                </div>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-1.5 text-gray-300 hover:text-white mobile-focus rounded-lg hover:bg-white/10 transition-all duration-200"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="lg:hidden border-t border-white/20"
            >
              <div className="py-2 mobile-spacing">
                {/* Navigation Items */}
                <div className="mobile-spacing">
                  {navItems.map((item) => {
                    const Icon = item.icon
                    const isActive = location.pathname === item.path
                    
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setIsMenuOpen(false)}
                        className={`mobile-nav-item mobile-focus ${
                          isActive
                            ? 'mobile-nav-item-active'
                            : 'mobile-nav-item-inactive'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        <span className="font-medium text-sm">{item.label}</span>
                        {isActive && (
                          <div className="ml-auto w-1.5 h-1.5 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full" />
                        )}
                      </Link>
                    )
                  })}
                </div>
                
                {/* User Section */}
                {user ? (
                  <div className="pt-2 border-t border-white/20">
                    <div className="flex items-center space-x-3 p-3 glass-effect rounded-xl">
                      <div className="w-7 h-7 bg-gradient-to-r from-green-500 to-blue-500 rounded-full flex items-center justify-center mobile-shadow">
                        <span className="text-white text-xs font-medium">
                          {user.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="flex-1">
                        <div className="text-white font-medium text-sm">{user.name}</div>
                        <div className="text-gray-400 text-xs">Welcome back!</div>
                      </div>
                      <button
                        onClick={() => {
                          signOut()
                          setIsMenuOpen(false)
                        }}
                        className="p-1.5 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-all duration-200"
                        title="Sign Out"
                      >
                        <LogOut className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="pt-2 border-t border-white/20 space-y-2">
                    <Link
                      to="/login"
                      onClick={() => setIsMenuOpen(false)}
                      className="mobile-button-secondary text-sm w-full text-center"
                    >
                      Sign In
                    </Link>
                    <Link
                      to="/signup"
                      onClick={() => setIsMenuOpen(false)}
                      className="mobile-button-primary text-sm w-full text-center"
                    >
                      Get Started
                    </Link>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      
      {/* User Status Modal */}
      <UserStatusModal
        isOpen={isStatusModalOpen}
        onClose={() => setIsStatusModalOpen(false)}
        userStatus={userStatus}
        onAutoImport={handleAutoImport}
        isImporting={isImporting}
      />
    </motion.nav>
  )
}

export default Navbar 